import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, ComposedChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const Card = ({ title, children }) => (
  <div className="bg-white border-2 border-black rounded-xl p-4">
    <h3 className="font-bold text-sm mb-2 text-black">{title}</h3>
    <div style={{ height: 240 }}>{children}</div>
  </div>
);

export default function AdminTraction() {
  const [d, setD] = useState(null);
  const [months, setMonths] = useState(7);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/traction?months=${months}`).then((r) => setD(r.data))
      .catch(() => toast.error("Failed to load traction data"));
  }, [months]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/traction/pdf?months=${months}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "chatity-traction.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF failed"); }
    setDownloading(false);
  };

  if (!d) return <div className="py-10 text-center text-gray-500">Loading traction…</div>;

  const rows = d.months.map((m, i) => ({
    m,
    retention: d.retention[i],
    cost: d.ai_cost_per_salon[i],
    new: d.clients.new[i],
    existing: d.clients.existing[i],
    reactivation: d.clients.reactivation[i],
    revenue: d.revenue[i],
    arppu: d.arppu[i],
  }));
  const k = d.kpis;

  return (
    <div className="space-y-4 text-black pt-4" data-testid="admin-traction">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-black text-black">TRACTION</h2>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
          className="border-2 border-black rounded-lg px-2 py-1 text-sm bg-white text-black" data-testid="traction-months-select">
          {[6, 7, 9, 12].map((n) => <option key={n} value={n}>{n} months</option>)}
        </select>
        <Button onClick={downloadPdf} disabled={downloading} data-testid="traction-pdf-btn"
          className="ml-auto bg-black text-white font-semibold">
          <Download className="w-4 h-4 mr-2" /> {downloading ? "…" : "Download PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          ["Salons", k.total_salons], ["Paying now", k.paying_salons_now],
          ["Avg retention", `${k.avg_retention}%`], ["Trial → paid", `${k.trial_to_paid}%`],
          ["ARPPU", `€${k.arppu_now}`], ["Revenue (month)", `€${k.revenue_last_month}`],
        ].map(([t, v]) => (
          <div key={t} className="bg-white border-2 border-black rounded-xl p-3 text-center">
            <div className="text-xl font-black text-black">{v}</div>
            <div className="text-[11px] text-gray-500 font-semibold">{t}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Retention (paying salons, month-over-month, %)">
          <ResponsiveContainer>
            <AreaChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="m" fontSize={11} /><YAxis domain={[0, 100]} fontSize={11} /><Tooltip />
              <Area dataKey="retention" stroke="#2f80ed" fill="#2f80ed" fillOpacity={0.25} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Est. AI cost per salon, $ (tokens estimated)">
          <ResponsiveContainer>
            <AreaChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="m" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
              <Area dataKey="cost" stroke="#eb5757" fill="#eb5757" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Clients by New / Existing / Reactivation (bookings)">
          <ResponsiveContainer>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="m" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
              <Line dataKey="existing" stroke="#f2994a" strokeWidth={2} />
              <Line dataKey="new" stroke="#2f80ed" strokeWidth={2} />
              <Line dataKey="reactivation" stroke="#5b4bc4" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Revenue by month, € (bars) + ARPPU, € (line)">
          <ResponsiveContainer>
            <ComposedChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="m" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
              <Bar dataKey="revenue" fill="#6fcf97" />
              <Line dataKey="arppu" stroke="#5b4bc4" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>
      <p className="text-xs text-gray-500">{d.assumptions}</p>
    </div>
  );
}
